import Phaser from 'phaser';
import { EventBus } from '../utils/EventBus';

/**
 * GameOverOverlay - Full screen game over display
 * Shows when player dies with options to return to menu or try again
 */
export class GameOverOverlay {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private background!: Phaser.GameObjects.Graphics;
  private gameOverText!: Phaser.GameObjects.Text;
  private reasonText!: Phaser.GameObjects.Text;
  private hitAreas: Phaser.GameObjects.Rectangle[] = [];
  private isVisible = false;

  /** Fade in duration in ms */
  private fadeInDuration = 1000;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.createOverlay();
    this.setupEventListeners();
    this.hide();
  }

  private createOverlay(): void {
    const { width, height } = this.scene.cameras.main;

    this.container = this.scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(2000);

    // Semi-transparent dark background
    this.background = this.scene.add.graphics();
    this.background.fillStyle(0x000000, 0.85);
    this.background.fillRect(0, 0, width, height);
    this.container.add(this.background);

    // Game over text
    this.gameOverText = this.scene.add
      .text(width / 2, height / 3, 'GAME OVER', {
        fontFamily: 'monospace',
        fontSize: '64px',
        color: '#ff4444',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.container.add(this.gameOverText);

    // Reason text (below game over)
    this.reasonText = this.scene.add
      .text(width / 2, height / 3 + 80, '', {
        fontFamily: 'monospace',
        fontSize: '24px',
        color: '#cccccc',
      })
      .setOrigin(0.5);
    this.container.add(this.reasonText);

    // Create buttons - add elements directly to main container (no nesting)
    const buttonY = height / 2 + 50;
    const buttonSpacing = 200;

    this.createButton(
      width / 2 - buttonSpacing / 2,
      buttonY,
      'Try Again',
      () => this.handleRetry()
    );

    this.createButton(
      width / 2 + buttonSpacing / 2,
      buttonY,
      'Main Menu',
      () => this.handleMenu()
    );
  }

  private createButton(
    x: number,
    y: number,
    text: string,
    onClick: () => void
  ): void {
    // Button background - added directly to main container
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x333333, 1);
    bg.fillRoundedRect(x - 80, y - 25, 160, 50, 8);
    bg.lineStyle(2, 0x666666, 1);
    bg.strokeRoundedRect(x - 80, y - 25, 160, 50, 8);
    this.container.add(bg);

    // Button text
    const buttonText = this.scene.add
      .text(x, y, text, {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    this.container.add(buttonText);

    // Interactive zone - added directly to scene (not container) for reliable hit detection
    const hitArea = this.scene.add.rectangle(x, y, 160, 50, 0x000000, 0);
    hitArea.setScrollFactor(0);
    hitArea.setDepth(2001);
    hitArea.setInteractive({ useHandCursor: true });
    this.hitAreas.push(hitArea);

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x444444, 1);
      bg.fillRoundedRect(x - 80, y - 25, 160, 50, 8);
      bg.lineStyle(2, 0x888888, 1);
      bg.strokeRoundedRect(x - 80, y - 25, 160, 50, 8);
    });

    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x333333, 1);
      bg.fillRoundedRect(x - 80, y - 25, 160, 50, 8);
      bg.lineStyle(2, 0x666666, 1);
      bg.strokeRoundedRect(x - 80, y - 25, 160, 50, 8);
    });

    hitArea.on('pointerdown', onClick);
  }

  private setupEventListeners(): void {
    EventBus.onEvent('game:over', (data) => {
      this.show(data.reason);
    });
  }

  /**
   * Show the game over overlay with fade in
   */
  public show(reason: string = 'You were defeated'): void {
    if (this.isVisible) return;

    this.isVisible = true;
    this.reasonText.setText(reason);
    this.container.setVisible(true);
    this.container.setAlpha(0);

    // Show hit areas
    for (const hitArea of this.hitAreas) {
      hitArea.setVisible(true);
      hitArea.setInteractive({ useHandCursor: true });
    }

    // Fade in animation
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: this.fadeInDuration,
      ease: 'Power2',
    });
  }

  /**
   * Hide the overlay
   */
  public hide(): void {
    this.isVisible = false;
    this.container.setVisible(false);
    this.container.setAlpha(0);

    // Hide hit areas
    for (const hitArea of this.hitAreas) {
      hitArea.setVisible(false);
      hitArea.disableInteractive();
    }
  }

  /**
   * Handle retry button click - restart the game scene completely
   */
  private handleRetry(): void {
    console.log('Retry clicked - restarting game');

    this.hide();

    // Restart the game scene completely to reset all state
    this.scene.scene.restart();
  }

  /**
   * Handle menu button click - return to main menu
   */
  private handleMenu(): void {
    console.log('Menu clicked - returning to main menu');

    this.hide();

    // Stop the game scene and go to main menu (not preloader)
    this.scene.scene.stop('GameScene');
    this.scene.scene.start('MainMenuScene');
  }

  /**
   * Check if overlay is currently visible
   */
  public getIsVisible(): boolean {
    return this.isVisible;
  }

  public destroy(): void {
    // Destroy hit areas
    for (const hitArea of this.hitAreas) {
      hitArea.destroy();
    }
    this.hitAreas = [];

    this.container.destroy(true);
  }
}
